import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const API_BASE = process.env.REACT_APP_API_URL;

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const avatarColors = ['#FF6B35', '#4CAF50', '#2196F3', '#9C27B0', '#FF9800', '#00BCD4', '#E91E63'];
const getAvatarColor = (name) => {
  if (!name) return avatarColors[0];
  const idx = name.charCodeAt(0) % avatarColors.length;
  return avatarColors[idx];
};

const Community = () => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [socket, setSocket] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load chat history
  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/feed`, { headers });
      // Use feed as a simple history indicator; chat messages come from socket
      // Actually load last chat messages from a separate endpoint or use feed
    } catch (err) {
      console.error('Load messages error:', err);
    }
  }, [token]);

  useEffect(() => {
    // Initialize socket
    const sock = io(API_BASE, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    sock.on('connect', () => {
      console.log('Connected to community chat');
    });

    sock.on('new_message', (msg) => {
      setMessages((prev) => [...prev, msg]);
    });

    sock.on('user_typing', (data) => {
      if (data.userId !== user?.id) {
        setTypingUsers((prev) => {
          if (!prev.find(u => u.userId === data.userId)) {
            return [...prev, data];
          }
          return prev;
        });
        setTimeout(() => {
          setTypingUsers((prev) => prev.filter(u => u.userId !== data.userId));
        }, 3000);
      }
    });

    sock.on('connect_error', (err) => {
      console.log('Socket connection error:', err.message);
    });

    setSocket(sock);
    setLoading(false);

    return () => {
      sock.disconnect();
    };
  }, [user?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!newMessage.trim() || !socket || sending) return;
    setSending(true);

    socket.emit('send_message', {
      userId: user?.id,
      userName: user?.name,
      message: newMessage.trim()
    });

    setNewMessage('');
    setSending(false);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    socket.emit('stop_typing', { userId: user?.id, userName: user?.name });

    inputRef.current?.focus();
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    if (socket) {
      socket.emit('typing', { userId: user?.id, userName: user?.name });
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('stop_typing', { userId: user?.id, userName: user?.name });
      }, 2000);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const dateKey = formatDate(msg.created_at);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(msg);
    return groups;
  }, {});

  return (
    <div className="page-container" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        padding: '16px',
        color: 'white',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '44px',
            height: '44px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px'
          }}>
            💬
          </div>
          <div>
            <h2 style={{ color: 'white', fontSize: '18px', marginBottom: '2px' }}>Community Chat</h2>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
              Talk with your SaathiCircle friends
            </p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px',
        paddingBottom: '8px',
        background: '#FFF8F0',
        minHeight: 0,
        maxHeight: 'calc(100vh - 220px)'
      }}>
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p style={{ color: '#FF6B35' }}>Connecting to community chat...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <div className="empty-title">No messages yet</div>
            <div className="empty-text">Say namaste to the community! 🙏</div>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date}>
              {/* Date separator */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '12px 0 8px'
              }}>
                <div style={{ flex: 1, height: '1px', background: '#E8E0D8' }} />
                <span style={{
                  fontSize: '12px',
                  color: '#999',
                  fontWeight: '500',
                  background: '#FFF8F0',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  border: '1px solid #E8E0D8'
                }}>{date}</span>
                <div style={{ flex: 1, height: '1px', background: '#E8E0D8' }} />
              </div>

              {msgs.map((msg) => {
                const isMe = msg.user_id === user?.id;
                return (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '10px',
                      flexDirection: isMe ? 'row-reverse' : 'row',
                      alignItems: 'flex-end'
                    }}
                  >
                    {/* Avatar */}
                    {!isMe && (
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: getAvatarColor(msg.user_name),
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        {getInitials(msg.user_name)}
                      </div>
                    )}

                    {/* Message Bubble */}
                    <div style={{ maxWidth: '75%' }}>
                      {!isMe && (
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '3px', fontWeight: '600' }}>
                          {msg.user_name}
                        </div>
                      )}
                      <div style={{
                        background: isMe ? '#FF6B35' : 'white',
                        color: isMe ? 'white' : '#2D2D2D',
                        padding: '10px 14px',
                        borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        fontSize: '15px',
                        lineHeight: '1.5',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                        wordBreak: 'break-word'
                      }}>
                        {msg.message}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#bbb',
                        marginTop: '3px',
                        textAlign: isMe ? 'right' : 'left'
                      }}>
                        {formatTime(msg.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}

        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{
              background: 'white',
              padding: '8px 14px',
              borderRadius: '16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
              display: 'flex',
              gap: '4px',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '13px', color: '#888' }}>
                {typingUsers[0]?.userName} is typing
              </span>
              <div style={{ display: 'flex', gap: '3px' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: '6px', height: '6px', background: '#ccc', borderRadius: '50%',
                    animation: `bounce 1s ${i * 0.2}s infinite`
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div style={{
        padding: '10px 12px',
        background: 'white',
        borderTop: '1px solid #E8E0D8',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end',
        position: 'sticky',
        bottom: '68px',
        zIndex: 100
      }}>
        <div className="avatar" style={{ background: '#FF6B35', width: '36px', height: '36px', fontSize: '14px', flexShrink: 0 }}>
          {getInitials(user?.name)}
        </div>
        <div style={{ flex: 1, position: 'relative' }}>
          <textarea
            ref={inputRef}
            value={newMessage}
            onChange={handleTyping}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: '20px',
              border: '2px solid #E8E0D8',
              fontFamily: "'Hind', 'Poppins', sans-serif",
              fontSize: '15px',
              resize: 'none',
              outline: 'none',
              lineHeight: '1.4',
              maxHeight: '100px',
              overflowY: 'auto',
              background: '#FFF8F0',
              color: '#2D2D2D'
            }}
            onFocus={(e) => e.target.style.borderColor = '#FF6B35'}
            onBlur={(e) => e.target.style.borderColor = '#E8E0D8'}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!newMessage.trim() || sending}
          style={{
            width: '44px',
            height: '44px',
            background: newMessage.trim() ? '#FF6B35' : '#E8E0D8',
            border: 'none',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s',
            fontSize: '20px',
            flexShrink: 0
          }}
        >
          ➤
        </button>
      </div>

      <BottomNav />

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
};

export default Community;
